import React, { useEffect, useState } from 'react';
import Header from '../Header';
import { Table, Form, Button, Row, Col } from 'react-bootstrap';
import { API_URL } from '../Config';
import Swal from 'sweetalert2';

// Custom CSS styles for better appearance
const customStyles = `
  .settings-checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 15px;
  }
  
  .custom-checkbox {
    display: flex;
    align-items: flex-start;
    margin-bottom: 8px;
  }
  
  .custom-checkbox .form-check-input {
    width: 18px;
    height: 18px;
    margin-right: 10px;
    margin-top: 2px;
    cursor: pointer;
    flex-shrink: 0;
  }
  
  .custom-checkbox .form-check-label {
    font-size: 14px;
    font-weight: 400;
    color: #495057;
    cursor: pointer;
    line-height: 1.4;
    margin-bottom: 0;
  }
  
  .custom-checkbox .form-check-input:checked {
    background-color: #dc3545;
    border-color: #dc3545;
  }
  
  .custom-checkbox .form-check-input:focus {
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
  
  .settings-input-group {
    margin-top: 15px;
    margin-bottom: 10px;
  }
  
  .settings-input-group:first-of-type {
    margin-top: 20px;
  }
  
  .form-label {
    color: #495057;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
    display: block;
  }
  
  .form-control, .form-select {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 8px 12px;
    font-size: 14px;
    height: auto;
  }
  
  .form-control:focus, .form-select:focus {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  }
  
  .table {
    margin-bottom: 30px;
  }
  
  .table th {
    background-color: #f8f9fa;
    border-color: #dee2e6;
    font-weight: 600;
    color: #495057;
    vertical-align: middle;
    padding: 15px;
  }
  
  .table td {
    vertical-align: top;
    padding: 20px 15px;
    border-color: #dee2e6;
  }
  
  .table td:first-child {
    font-weight: 500;
    color: #495057;
    background-color: #f8f9fa;
    width: 200px;
  }
  
  .btn-danger {
    background-color: #dc3545;
    border-color: #dc3545;
    font-weight: 500;
    padding: 10px 24px;
    border-radius: 4px;
  }
  
  .btn-danger:hover {
    background-color: #c82333;
    border-color: #bd2130;
  }
  
  .main-container {
    padding: 20px 0;
  }
  
  .settings-section-title {
    font-size: 24px;
    font-weight: 600;
    color: #212529;
    margin-bottom: 20px;
  }
  
  .input-small {
    width: 150px;
    max-width: 100%;
  }
  
  .input-medium {
    width: 250px;
    max-width: 100%;
  }
  
  .input-large {
    width: 300px;
    max-width: 100%;
  }
`;

export default function CompanySettings() {
  const role = localStorage.getItem('user_role');
  const accessToken = localStorage.getItem("accessToken");

  const [formData, setFormData] = useState({
    product_list_sorting: 'code',
    product_type: 'box',
    invoice_discount: 'Y',
    add_new_customer: 'Y',
    maxDiscountAllowedOnInvoice: '100',
    checkStockBeforeAddToCart: 'N',
    displayProductStock: 'Y',
    displayProductWithZeroStock: 'Y',
    canAddHalfBoxWhileOrder: 'N',
    showCustomerNameInPicking: 'Y',
    locationAccessPermissionMessage: 'N',
    canShareInvoice: 'Y',
    grnExpiryDateIsMandatory: 'Y',
    grnAcceptExcessQuantity: 'N',
    isSingleOrderPicking: 'N',
    isAutogenProductCode: 'N',
    enableDsr: 'Y',
    shopifyConnectivity: 'N',
    isShopifyVatInclusiveOrExclusive: '',
    showEmailTemplate: 'Y',
    amazonConnectivity: 'N',
    isAmazonVatInclusiveOrExclusive: 'N',
    taxType: 'VAT',
    currencySymbol: '£',
    timezone: 'Europe/London',
    showContraPrice: 'Y',
    isShowRutePlan: 'N',
    showCessRate: 'N',
    howmanyInvoiceCanPrint: 1 // Default to 1, but null/0 will fallback to default behavior
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'howmanyInvoiceCanPrint') {
      // Handle invoice copies field - allow empty/null for default behavior
      const numValue = value === '' ? null : parseInt(value) || 0;
      setFormData(prev => ({
        ...prev,
        [name]: numValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (checked ? 'Y' : 'N') : value
      }));
    }
  };

  const getCompanySettings = async () => {
    const response = await fetch(`${API_URL}/settings/ws-get-company-settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      }
    });
    let data = await response.json();
    data = data?.data;
    console.log('API response:', data);

    // Handle howmanyInvoiceCanPrint field - ensure it's a valid number or null
    if (data && data.howmanyInvoiceCanPrint !== undefined) {
      const copiesValue = parseInt(data.howmanyInvoiceCanPrint);
      data.howmanyInvoiceCanPrint = isNaN(copiesValue) || copiesValue <= 0 ? null : copiesValue;
    }

    setFormData(data);
  };

  useEffect(() => {
    getCompanySettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Prepare data for submission, handling null values for howmanyInvoiceCanPrint
      const submitData = {
        ...formData,
        howmanyInvoiceCanPrint: formData.howmanyInvoiceCanPrint || null
      };

      const response = await fetch(`${API_URL}/settings/ws-edit-company-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();
      console.log('API response:', data);

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Company settings updated successfully.',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to save settings',
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An unexpected error occurred while saving settings.',
      });
    }
  };

  return (
    <div>
      <style>{customStyles}</style>
      <Header />
      <div className="breadcrumbs">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <a href="#"><i className="fa fa-home"></i></a> &gt;
              <span className="last-crumb">Company Settings</span>
            </div>
          </div>
        </div>
      </div>

      <div className="main-container">
        <div className="container-fluid">
          <Form onSubmit={handleSubmit}>
            <Table bordered responsive>
              <thead>
                <tr>
                  <th style={{ width: "200px" }}>Module Name</th>
                  <th>Settings</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Product Settings</td>
                  <td>
                    <div className="settings-checkbox-group">
                      <Form.Check
                        type="checkbox"
                        label="Can System Auto-Generate Product Codes?"
                        name="isAutogenProductCode"
                        checked={formData.isAutogenProductCode === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Should Product Stock be Displayed to Users?"
                        name="displayProductStock"
                        checked={formData.displayProductStock === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Can Products with Zero Stock be Displayed?"
                        name="displayProductWithZeroStock"
                        checked={formData.displayProductWithZeroStock === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Should Stock be Checked Before Adding to Cart?"
                        name="checkStockBeforeAddToCart"
                        checked={formData.checkStockBeforeAddToCart === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Should Cess Rate be Shown on Products?"
                        name="showCessRate"
                        checked={formData.showCessRate === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>Order Settings</td>
                  <td>
                    <div className="settings-checkbox-group">
                      <Form.Check
                        type="checkbox"
                        label="Can Users Apply Discounts on Invoices?"
                        name="invoice_discount"
                        checked={formData.invoice_discount === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Can Users Add New Customers During Orders?"
                        name="add_new_customer"
                        checked={formData.add_new_customer === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Can Users Add Half Boxes While Ordering?"
                        name="canAddHalfBoxWhileOrder"
                        checked={formData.canAddHalfBoxWhileOrder === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Should Customer Names be Shown During Picking?"
                        name="showCustomerNameInPicking"
                        checked={formData.showCustomerNameInPicking === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Can Users Do Single Order Picking?"
                        name="isSingleOrderPicking"
                        checked={formData.isSingleOrderPicking === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                    </div>

                    <div className="settings-input-group">
                      <Form.Label className='fw-bold'>Maximum Discount Allowed on Invoice (%)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter percentage"
                        name="maxDiscountAllowedOnInvoice"
                        value={formData.maxDiscountAllowedOnInvoice}
                        onChange={handleChange}
                        className="input-small w-25"
                        min="0"
                        max="100"
                      />
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>Integration Settings</td>
                  <td>
                    <div className="settings-checkbox-group">
                      <Form.Check
                        type="checkbox"
                        label="Is Shopify Integration Enabled?"
                        name="shopifyConnectivity"
                        checked={formData.shopifyConnectivity === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Is Amazon Integration Enabled?"
                        name="amazonConnectivity"
                        checked={formData.amazonConnectivity === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Is DSR (Direct Sales Representative) Feature Enabled?"
                        name="enableDsr"
                        checked={formData.enableDsr === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                    </div>
                    <Row>
                      <Col md={6} className='p-0'>
                        <div className="settings-input-group">
                          <Form.Label className='fw-bold'>Shopify VAT Calculation Method</Form.Label>
                          <Form.Select
                            name="isShopifyVatInclusiveOrExclusive"
                            value={formData.isShopifyVatInclusiveOrExclusive}
                            onChange={handleChange}
                            className="input-medium"
                          >
                            <option value="">Select VAT calculation method</option>
                            <option value="Y">VAT Inclusive (Price includes VAT)</option>
                            <option value="N">VAT Exclusive (VAT added to price)</option>
                          </Form.Select>
                        </div>
                      </Col>
                      <Col md={6}>

                        <div className="settings-input-group">
                          <Form.Label className='fw-bold'>Amazon VAT Calculation Method</Form.Label>
                          <Form.Select
                            name="isAmazonVatInclusiveOrExclusive"
                            value={formData.isAmazonVatInclusiveOrExclusive}
                            onChange={handleChange}
                            className="input-medium"
                          >
                            <option value="">Select VAT calculation method</option>
                            <option value="Y">VAT Inclusive (Price includes VAT)</option>
                            <option value="N">VAT Exclusive (VAT added to price)</option>
                          </Form.Select>
                        </div>
                      </Col>
                    </Row>
                  </td>
                </tr>

                <tr>
                  <td>Invoice Settings</td>
                  <td>
                    <div className="settings-input-group">
                      <Form.Label className='fw-bold'>Number of Invoice Copies to Print</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="Enter number (1-10, or leave empty for default)"
                        name="howmanyInvoiceCanPrint"
                        value={formData.howmanyInvoiceCanPrint || ''}
                        onChange={handleChange}
                        className="input-small w-25"
                        min="1"
                        max="10"
                      />
                    </div>
                    
                  </td>
                </tr>

                <tr>
                  <td>Other Settings</td>
                  <td>
                    <div className="settings-checkbox-group">
                      <Form.Check
                        type="checkbox"
                        label="Should Email Templates be Visible to Users?"
                        name="showEmailTemplate"
                        checked={formData.showEmailTemplate === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Should Contra Price be Displayed?"
                        name="showContraPrice"
                        checked={formData.showContraPrice === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                      <Form.Check
                        type="checkbox"
                        label="Should Route Planning be Visible to Users?"
                        name="isShowRutePlan"
                        checked={formData.isShowRutePlan === 'Y'}
                        onChange={handleChange}
                        className="custom-checkbox"
                      />
                    </div>

                    <Row>
                      <Col md={6} className='ps-0'>
                        <div className="settings-input-group">
                          <Form.Label className='fw-bold'>Tax Type (E.G., VAT, GST, Sales Tax)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter tax type"
                            name="taxType"
                            value={formData.taxType}
                            onChange={handleChange}
                            className="input-medium"
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="settings-input-group">
                          <Form.Label className='fw-bold'>Currency Symbol (E.G., £, $, €)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter currency symbol"
                            name="currencySymbol"
                            value={formData.currencySymbol}
                            onChange={handleChange}
                            className="input-small"
                          />
                        </div>
                      </Col>
                    </Row>

                    <div className="settings-input-group">
                      <Form.Label className='fw-bold'>Timezone (E.G., Europe/London, America/New_York)</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter timezone"
                        name="timezone"
                        value={formData.timezone}
                        onChange={handleChange}
                        className="input-large"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>

            <div className="d-flex justify-content-start">
              <Button variant="danger" type="submit">Save Settings</Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
}